// T2T-05-C5 — Bell SSE subscriber. Same backoff pattern as the chat SSE hook.

import { useEffect, useRef, useState } from "react";
import { buildBellStreamUrl } from "../../../api/t2tNotificationsApi";

const RETRY_DELAYS = [1000, 2000, 4000, 8000, 15000];
const MAX_ATTEMPTS = RETRY_DELAYS.length;

/**
 * @param {{ onNotificationNew: (row) => void }} handlers
 */
export default function useBellSse(handlers) {
  const [status, setStatus] = useState("connecting");
  const esRef        = useRef(null);
  const attemptsRef  = useRef(0);
  const closedRef    = useRef(false);
  const handlersRef  = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    closedRef.current = false;

    const open = () => {
      if (closedRef.current) return;
      const es = new EventSource(buildBellStreamUrl());
      esRef.current = es;
      setStatus("connecting");

      es.addEventListener("stream.open", () => {
        setStatus("connected");
        attemptsRef.current = 0;
      });

      es.addEventListener("notification.new", (e) => {
        try {
          const row = JSON.parse(e.data);
          if (row) handlersRef.current.onNotificationNew?.(row);
        } catch (_) { /* swallow */ }
      });

      es.onerror = () => {
        if (closedRef.current) return;
        try { es.close(); } catch (_) { /* gone */ }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        const delay = RETRY_DELAYS[attemptsRef.current];
        attemptsRef.current += 1;
        setStatus("disconnected");
        setTimeout(open, delay);
      };
    };

    open();

    return () => {
      closedRef.current = true;
      if (esRef.current) {
        try { esRef.current.close(); } catch (_) { /* noop */ }
        esRef.current = null;
      }
    };
  }, []);

  return { status };
}
